import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';

import {CredentialsService} from './credentials.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {map} from 'rxjs/operators';


export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  redirectUrl: string;
  public tokenSubject: BehaviorSubject<any>;
  public token: Observable<any>;
  public refreshTokenTimeout : any;

  constructor(private credentialsService: CredentialsService, private http: HttpClient, private router: Router) {
    this.tokenSubject = new BehaviorSubject<any>(null);
    this.token = this.tokenSubject.asObservable();
  }
  public GRANT_TYPE_REFRESH = 'refresh_token';
  public GRANT_TYPE = 'password';
  public GET_TOKEN = '/oauth/token';
  public HEADER = 'gls-web:gls-secret';

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<any> {
    return this.loadAccessToken(true, context.username, context.password);
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    // Customize credentials invalidation here
    this.credentialsService.setCredentials();
    this.stopRefreshTokenTimer();
    this.tokenSubject.next(null);
    return of(true);
  }
  /**
   * gets the new access token using refresh token
   * and sets it in the localstorage again else logs out
   */
  fetchNewAccessToken(name: string): Observable<any> {
    return new Observable<boolean>((observer) => {
        this.loadAccessToken(false).subscribe((response: any) => {
            const data = { username: name, token: response.access_token,
          refreshtoken: response.refresh_token, expiry: response.expires_in ? response.expires_in : JSON.parse(atob(response.access_token.split('.')[1])).exp
            };
            this.credentialsService.setCredentials(data, true);
            observer.next(true);
          },
          error => {
            this.logout().subscribe(() => this.router.navigate(['/login'], { replaceUrl: true }));
            observer.next( false);
          });
      }

    );

  }


  private loadAccessToken(retrieveAccessToken: boolean, username?: string, password?: string): Observable<any> {

    const formData =   new FormData();
    const refreshFormData = new FormData();
    if (retrieveAccessToken) {
      formData.append('grant_type', this.GRANT_TYPE);
      formData.append('password', password);
      formData.append('username', username);
    } else {
      refreshFormData.append('grant_type', this.GRANT_TYPE_REFRESH);
      refreshFormData.append('refresh_token', this.credentialsService.credentials.refreshtoken);
    }
    const headers = new HttpHeaders({
      Authorization: 'Basic ' + btoa(this.HEADER)
    });
    return this.http.post<any>(this.GET_TOKEN, retrieveAccessToken ? formData : refreshFormData, {headers})
      .pipe(map(res => {
        const data = {
          username: username, token: res.access_token,
          refreshtoken: res.refresh_token, expiry: res.expires_in ? res.expires_in : JSON.parse(atob(res.access_token.split('.')[1])).exp
        };
        this.credentialsService.setCredentials(data, true);
        this.tokenSubject.next(res);
        // this.startRefreshTokenTimer(JSON.parse(atob(res.access_token.split('.')[1])).exp);
        return res;
      }));
  }
  public startRefreshTokenTimer(expiry : any) {
    // set a timeout to refresh the token 5 minutes before it expires
    const expires = new Date(expiry * 1000);
    const timeout = expires.getTime() - Date.now() - (5 * 60 * 1000);
    this.refreshTokenTimeout = setTimeout(() => this.loadAccessToken(false).subscribe(), timeout);

  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  setHeader(header: string) {
    this.HEADER = header;
  }

  setGrantType(grantType: string) {
    this.GRANT_TYPE = grantType;
  }

  setGetTokenUrl(token: string) {
    this.GET_TOKEN = token;
  }

  setGrantTypeRefresh(grantType: string) {
    this.GRANT_TYPE_REFRESH = grantType;
  }
}
